import { DELETE_CONFIRM_PHRASES, DELETE_MAPPING_COPY } from "../constants/deleteMapping.constants";
import { DeleteMappingMode, DeleteMappingScope } from "../types/deleteMapping.types";

export const resolveRequiredPhrase = (scope: DeleteMappingScope): string => DELETE_CONFIRM_PHRASES[scope];

/** Case-insensitive and whitespace-tolerant, so "  Remove All  " counts as confirmed. */
export const matchesConfirmPhrase = (input: string, requiredPhrase: string): boolean =>
  input.trim().toLowerCase() === requiredPhrase.toLowerCase();

export const resolveDestructiveLabel = (scope: DeleteMappingScope): string =>
  scope === DeleteMappingScope.CurrentVersion
    ? DELETE_MAPPING_COPY.submitCurrentVersion
    : DELETE_MAPPING_COPY.submitAllVersions;

/**
 * The single place that turns "how many versions" and "do we know which is active" into one of
 * the four `DeleteMappingMode`s. Everything else in the dialog -- which option is disabled, what
 * scope is forced, which endpoint a "current version" submit actually hits, what copy is shown --
 * is derived from this one value instead of re-deriving the same two facts independently.
 *
 * A mapping can exist (and show the Delete button) before its first version is ever saved --
 * versions are only created by the separate "Edit"/create-version flow -- so `currentVersionId`
 * being absent is a real, reachable state, not just a loading race.
 */
export const resolveDeleteMode = (
  versionCount: number,
  currentVersionId: string | undefined
): DeleteMappingMode => {
  const hasCurrentVersion = !!currentVersionId;

  if (!hasCurrentVersion) {
    return versionCount > 1 ? DeleteMappingMode.NoDefaultVersion : DeleteMappingMode.NoVersionRow;
  }
  return versionCount <= 1 ? DeleteMappingMode.LastVersion : DeleteMappingMode.Choice;
};

/** No version to target -- current-version-only cannot be selected. */
export const isCurrentVersionDisabled = (mode: DeleteMappingMode): boolean =>
  mode === DeleteMappingMode.NoDefaultVersion;

/** Exactly one version (and we know which) -- removing it already does what "all versions" would. */
export const isAllVersionsDisabled = (mode: DeleteMappingMode): boolean =>
  mode === DeleteMappingMode.LastVersion || mode === DeleteMappingMode.NoVersionRow;

/**
 * `LastVersion` and `NoVersionRow` both force "current version only", but only `NoVersionRow` has
 * no version id to send it to -- there, the submit must be routed to the mapping-delete endpoint
 * instead, even though the user sees the current-version option and phrase.
 */
export const usesMappingEndpoint = (mode: DeleteMappingMode): boolean => mode === DeleteMappingMode.NoVersionRow;

/**
 * The scope actually in force. Derived rather than stored, so a version count or current-version
 * id that arrives after the dialog opened cannot leave it stranded on a selection the user never
 * made -- and cannot leave it aimed at a scope this mode has disabled.
 */
export const resolveEffectiveScope = (selectedScope: DeleteMappingScope, mode: DeleteMappingMode): DeleteMappingScope => {
  if (isCurrentVersionDisabled(mode)) {
    return DeleteMappingScope.AllVersions;
  }
  if (isAllVersionsDisabled(mode)) {
    return DeleteMappingScope.CurrentVersion;
  }
  return selectedScope;
};
